/**
 * 實體兌換 api ( storeRedeemAPI ) 可預期的 MESSAGE
 * @typedef {Object} STORE_REDEEM_API_RESPONSE
 * @property {string} REDEEM_EXPIRED - 兌換時間已逾期[406]
 * @property {string} DUPLICATED_REDEMPTION - 獎品已核銷過[422]
 * @property {string} OTHER_ERROR - 其餘後端預期錯誤
 */
export const STORE_REDEEM_API_RESPONSE = {
    REDEEM_EXPIRED: (payload = {}) => payload?.message === 'redeem expired',
    DUPLICATED_REDEMPTION: (payload = {}) => payload?.message === 'duplicated redemption',
    OTHER_ERROR: (payload = {}) => {
        return (
            payload?.message === 'winner not found' || // 400
            payload?.message === 'setting not enabled' || // 400
            payload?.message === 'awardee form' || // 400
            payload?.message === 'condition is invalid' || // 400
            payload?.message === 'Redeem Within Months Last Day is empty' || // 400
            payload?.message === 'Redeem Within Date Time is empty' || // 400
            payload?.message === 'unknown error' || // 400
            payload?.message === 'unknown redeem time type' || // 422
            payload?.message === 'Redeem failed' // 422
        );
    }
};

/**
 * 取得得獎獎品狀態 api ( getPrizeWinnerStatusAPI ) 可預期的 MESSAGE
 * @typedef {Object} GET_PRIZE_WINNER_STATUS_API_RESPONSE
 * @property {string} WINNER_NOT_FOUND - 查無得獎紀錄
 */
export const GET_PRIZE_WINNER_STATUS_API_RESPONSE = {
    WINNER_NOT_FOUND: (payload = {}) => payload?.message === 'winner not found'
};

/**
 * StoreGameResult api ( submitForm / submitPromo ) 可預期的 MESSAGE
 * @typedef {Object} STORE_GAME_RESULT_API_RESPONSE
 * @property {string} ENTRY_FLOW_EXISTS - Entry Flow 已存在
 * @property {string} ENTRY_TOTAL_LIMIT - 人次方案額度已滿（總人次或每月人次）
 */
export const STORE_GAME_RESULT_API_RESPONSE = {
    ENTRY_FLOW_EXISTS: (payload = {}) =>
        payload?.message === 'Entry flow exists' && payload?.code === 'ETF-C0-FD0',
    // entry flow 驗證時發現 game result 已存在（後端 message 結尾可能帶句點，先移除再比對）
    ENTRY_FLOW_GAME_RESULT_EXISTS: (payload = {}) =>
        payload?.code === 'ETF-V0-F0' &&
        payload?.message?.replace(/\.$/, '') ===
            'Entry flow validate failed - game result is exists',
    // 人次方案額度在送出遊戲結果扣次時用盡（promotion-api 為 ENT-V0-F0；fever-api 舊制 code 為 400）
    ENTRY_TOTAL_LIMIT: (payload = {}) =>
        (payload?.code === 'ENT-V0-F0' || Number(payload?.code) === 400) &&
        [
            'Entry total limit - validate failed',
            'Total monthly entry limit - validate failed'
        ].includes(payload?.message)
};

/**
 * getUserPoints api 可預期的 MESSAGE
 * @typedef {Object} GET_USER_POINTS_API_RESPONSE
 * @property {string} THIRD_PARTY_TIMED_OUT - 對方 api 超過十秒沒有回應 ( cURL error 28 )
 */
export const GET_USER_POINTS_API_RESPONSE = {
    THIRD_PARTY_API_TIMED_OUT: (payload = {}) =>
        payload?.message?.includes('cURL error 28') && payload?.code === 'FVTS-3RD-F0'
};
/**
 * createNotificationApi api 可預期的 MESSAGE
 * @typedef {Object} POST_NOTIFY_API_RESPONSE
 * @see https://app.clickup.com/t/86eu1jhz6
 */
export const POST_NOTIFY_API_RESPONSE = {
    // 請求驗證錯誤
    MISSING_NOTIFICATION_REQUIRED_FIELDS: (payload = {}) =>
        payload?.message === 'validation failed' && payload?.code === 'CUDF-V0-F0',
    // LoginMethod 不支援的 SourceType
    LOGIN_METHOD_NO_SUPPORT_SOURCE_TYPE: (payload = {}) =>
        payload?.message === 'login method not support this source type' &&
        payload?.code === 'NTF-V0-F0',
    // 會員不存在
    MEMBER_NOT_FOUND: (payload = {}) =>
        payload?.message === 'member not found' && payload?.code === 'MB-R1-FNF1',
    // 會員 Email 格式無效
    MEMBER_EMAIL_NOT_VALID: (payload = {}) =>
        payload?.message === 'member email not valid' && payload?.code === 'MB-V0-F0',
    // Driver 不支援
    DRIVER_NOT_SUPPORT: (payload = {}) =>
        payload?.message === 'driver is not support' && payload?.code === 'NTF-V0-F0',
    // 註冊表單 Email 欄位輸入已存在
    DUPLICATED_CREATE_NOTIFICATION: (payload = {}) =>
        payload?.message ===
            'create notification failed, because sign up form email column input is exist' &&
        payload?.code === 'NTF-C0-F0',
    // 通知已建立過
    NOTIFICATION_EXIST: (payload = {}) =>
        payload?.message === 'create notification failed, because notification is exist' &&
        payload?.code === 'NTF-C0-F0',
    NOTIFY_CREATE_FAILED: httpStatus => httpStatus !== 200,
    NOTIFY_CREATE_FAILED_BUT_NO_SENTRY: payload =>
        POST_NOTIFY_API_RESPONSE.MISSING_NOTIFICATION_REQUIRED_FIELDS(payload) ||
        POST_NOTIFY_API_RESPONSE.LOGIN_METHOD_NO_SUPPORT_SOURCE_TYPE(payload) ||
        POST_NOTIFY_API_RESPONSE.MEMBER_NOT_FOUND(payload) ||
        POST_NOTIFY_API_RESPONSE.MEMBER_EMAIL_NOT_VALID(payload) ||
        POST_NOTIFY_API_RESPONSE.DRIVER_NOT_SUPPORT(payload) ||
        POST_NOTIFY_API_RESPONSE.DUPLICATED_CREATE_NOTIFICATION(payload) ||
        POST_NOTIFY_API_RESPONSE.NOTIFICATION_EXIST(payload)
};

/**
 * storeEntryShareRecord api 可預期的 MESSAGE
 * @typedef {Object} STORE_ENTRY_SHARE_RECORD_API_RESPONSE
 * @property {string} RECORD_FAILED - 寫入分享圖片記錄失敗（驗證失敗／Rate Limit／DB Transaction 失敗等）
 * @see https://app.clickup.com/t/86ex144t2
 */
export const STORE_ENTRY_SHARE_RECORD_API_RESPONSE = {
    RECORD_FAILED: ({ httpStatus } = {}) => httpStatus !== 200
};

/**
 * getWinner api 可預期的 MESSAGE
 * @typedef {Object} GET_WINNER_API_RESPONSE
 * @property {string} PROMO_PAUSED_NO_LOTTERY - Promotion 狀態為 Pause，沒有可配的 lottery box（視為 RUNOUT）
 * @property {string} PRIZE_NOT_FOUND - 獎品已被刪除/下架（LT-V0-F0，視為 RUNOUT）
 */
export const GET_WINNER_API_RESPONSE = {
    PROMO_PAUSED_NO_LOTTERY: (payload = {}) =>
        payload?.message === 'Promotion status is Pause - No lottery box',
    PRIZE_NOT_FOUND: (payload = {}) =>
        payload?.code?.toLowerCase() === 'lt-v0-f0' && payload?.message === 'prize uuid not found'
};

/**
 * retryAIJobApi 可預期的 MESSAGE
 * @typedef {Object} RETRY_AI_JOB_API_RESPONSE
 * @property {string} JOB_IS_PROCESSING - Job 正在處理中
 * @property {string} JOB_IS_ALREADY_SUCCESS - Job 已經成功
 * @property {string} RETRY_AI_JOB_FAILED - Job 重試失敗
 */
export const RETRY_AI_JOB_API_RESPONSE = {
    JOB_IS_NOT_FOUND: (payload = {}) =>
        payload?.code === 'EAIJ-R1-FNF1' && payload?.message === 'Entry AI job not found',
    JOB_IS_ALREADY_PENDING: (payload = {}) =>
        payload?.code === 'EAIJ-V0-F0' && payload?.message === 'Entry AI job is already pending',
    JOB_IS_ALREADY_PROCESSING: (payload = {}) =>
        payload?.code === 'EAIJ-V0-F0' && payload?.message === 'Entry AI job is already processing',
    JOB_IS_ALREADY_COMPLETED: (payload = {}) =>
        payload?.code === 'EAIJ-V0-F0' && payload?.message === 'Entry AI job is already completed',
    RETRY_AI_JOB_FAILED: ({ httpStatus, payload = {} }) =>
        !(
            RETRY_AI_JOB_API_RESPONSE.JOB_IS_NOT_FOUND(payload) ||
            RETRY_AI_JOB_API_RESPONSE.JOB_IS_ALREADY_PENDING(payload) ||
            RETRY_AI_JOB_API_RESPONSE.JOB_IS_ALREADY_PROCESSING(payload) ||
            RETRY_AI_JOB_API_RESPONSE.JOB_IS_ALREADY_COMPLETED(payload)
        ) && httpStatus !== 200
};
